import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import { cn } from "@/marketing/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import {
  type CampaignLimitation,
  type CreateInfluencerCampaignRequest,
  type InfluencerCampaign,
  type ProductAllocation,
  type ProductGroup,
  type UpdateInfluencerCampaignRequest,
  CAMPAIGN_REGION_OPTIONS,
  CAMPAIGN_REWARD_BADGE_LABEL,
  CampaignLimitationKind,
  CampaignRewardBadge,
  AFFILIATE_CAMPAIGN_PLATFORMS,
  INFLUENCER_CAMPAIGN_PLATFORM_LABEL,
  STANDARD_CAMPAIGN_PLATFORMS,
  INFLUENCER_CAMPAIGN_TYPE_LABEL,
  InfluencerCampaignPlatform,
  InfluencerCampaignStatus,
  InfluencerCampaignTrafficLight,
  InfluencerCampaignType,
  STATUS_META,
  STATUS_ORDER,
} from "@/marketing/models/influencerCampaign";
import {
  CAMPAIGN_PIPELINE_TYPE_LABEL,
  CampaignPipelineType,
  STANDARD_PIPELINE_TYPE,
} from "@/marketing/models/campaignPipeline";
import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import { INFLUENCER_CAMPAIGN_KEYS } from "@/marketing/infrastructure/influencer/campaign/utils/keys";
import { BrandSelect } from "./BrandSelect";
import { BriefStructuredSection } from "./BriefStructuredSection";
import { ProductPoolChoice } from "./ProductPoolChoice";
import { ProductGroupsEditor } from "./ProductGroupsEditor";
import { AffiliateTermsFields } from "./AffiliateTermsFields";
import {
  affiliateTermsErrors,
  buildAffiliateTerms,
  emptyAffiliateTermsForm,
  readAffiliateTermsForm,
  type AffiliateTermsFormState,
} from "./affiliateTermsForm";
import { buildCreateCampaignRequest, buildUpdateCampaignRequest } from "./buildCampaignRequest";
import {
  type BriefStructuredFormState,
  buildBriefStructured,
  emptyBriefStructuredForm,
  readBriefStructuredForm,
} from "./briefStructuredForm";
import {
  buildCardFields,
  emptyCardFieldsForm,
  readCardFieldsForm,
  type CardFieldsFormState,
} from "./cardFieldsForm";
import {
  createProductGroupId,
  validateProductGroupConfiguration,
} from "./productGroupForm";

type ProductSelectionMode = "flat" | "grouped";

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-foreground/80">
        {label}
        {required && <span className="tw-text-red-500"> *</span>}
        {hint && <span className="tw-ml-1 tw-font-normal tw-text-muted-foreground">· {hint}</span>}
      </label>
      {children}
    </div>
  );
}

// Section is "active" in the nav once its top is within this many px of the scroll-area top.
const SCROLL_SPY_OFFSET_PX = 120;

const SECTIONS = [
  { id: "basics", num: 1, title: "Basics", sub: "Name · schedule · regions" },
  { id: "brief", num: 2, title: "Campaign Brief", sub: "What creators read" },
  { id: "reward", num: 3, title: "Reward & Products", sub: "Type → payout → pool" },
] as const;

// Decorative icons mirroring the mockup type cards (aria-hidden so a11y names stay the model label).
const CAMPAIGN_TYPE_ICON: Record<InfluencerCampaignType, string> = {
  [InfluencerCampaignType.ProductReview]: "⭐",
  [InfluencerCampaignType.UgcReview]: "🎬",
  [InfluencerCampaignType.Affiliate]: "🔗",
  [InfluencerCampaignType.Sampling]: "🎁",
  [InfluencerCampaignType.Giveaway]: "🎉",
};

function SectionHeading({ num, title, badge }: { num: number; title: string; badge?: React.ReactNode }) {
  return (
    <h2 className="tw-mb-1 tw-flex tw-items-center tw-gap-2 tw-text-[15px] tw-font-bold">
      <span className="tw-inline-flex tw-h-[22px] tw-w-[22px] tw-items-center tw-justify-center tw-rounded-md tw-bg-primary tw-text-xs tw-font-extrabold tw-text-primary-foreground">
        {num}
      </span>
      {title}
      {badge}
    </h2>
  );
}

function regionLimitationValues(
  limitations: CampaignLimitation[] | undefined,
): string[] {
  const region = limitations?.find(
    (l) => l.kind === CampaignLimitationKind.Region,
  );
  return region?.values ?? [];
}

// Unknown backend pipeline types still render, using the raw type as their label.
function pipelineLabel(pipelineType: string): string {
  return (
    CAMPAIGN_PIPELINE_TYPE_LABEL[pipelineType as CampaignPipelineType] ??
    pipelineType
  );
}

interface NewCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultOwnerId: string;
  mode?: "create" | "edit";
  initialCampaign?: InfluencerCampaign | undefined;
}

export function NewCampaignDialog({
  open,
  onOpenChange,
  defaultOwnerId,
  mode = "create",
  initialCampaign,
}: NewCampaignDialogProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [pipelineType, setPipelineType] = useState<string>(
    STANDARD_PIPELINE_TYPE,
  );
  const [brandId, setBrandId] = useState("");
  const [status, setStatus] = useState<InfluencerCampaignStatus>(
    InfluencerCampaignStatus.NotStarted,
  );
  const [trafficLight, setTrafficLight] = useState<
    InfluencerCampaignTrafficLight | ""
  >("");
  const [onboardingTarget, setOnboardingTarget] = useState("");
  const [postTarget, setPostTarget] = useState("");
  const [postingDueDate, setPostingDueDate] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [brief, setBrief] = useState("");
  const [productRefs, setProductRefs] = useState<string[]>([]);
  const [productRefDraft, setProductRefDraft] = useState("");
  const [productAllocations, setProductAllocations] = useState<ProductAllocation[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [productSelectionMode, setProductSelectionMode] =
    useState<ProductSelectionMode>("flat");
  const [limitationRegions, setLimitationRegions] = useState<string[]>([]);

  // `brief` (textarea) doubles as briefStructured.coreMessage on submit.
  // The rest of the structured payload lives in this object — see Cindy ME-CD-01.
  const [briefForm, setBriefForm] = useState<BriefStructuredFormState>(
    emptyBriefStructuredForm(),
  );
  const [cardForm, setCardForm] = useState<CardFieldsFormState>(emptyCardFieldsForm());
  const [affiliateTermsForm, setAffiliateTermsForm] = useState<AffiliateTermsFormState>(
    emptyAffiliateTermsForm(),
  );

  const formScrollRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<(typeof SECTIONS)[number]["id"]>(
    SECTIONS[0].id,
  );

  function resetForm() {
    setName("");
    setPipelineType(STANDARD_PIPELINE_TYPE);
    setBrandId("");
    setStatus(InfluencerCampaignStatus.NotStarted);
    setTrafficLight("");
    setOnboardingTarget("");
    setPostTarget("");
    setPostingDueDate("");
    setStartAt("");
    setEndAt("");
    setBrief("");
    setProductRefs([]);
    setProductRefDraft("");
    setProductAllocations([]);
    setProductGroups([]);
    setProductSelectionMode("flat");
    setLimitationRegions([]);
    setBriefForm(emptyBriefStructuredForm());
    setCardForm(emptyCardFieldsForm());
    setAffiliateTermsForm(emptyAffiliateTermsForm());
  }

  useEffect(() => {
    if (mode === "edit" && initialCampaign) {
      setName(initialCampaign.name);
      setPipelineType(initialCampaign.pipelineType ?? STANDARD_PIPELINE_TYPE);
      setBrandId(initialCampaign.brandId);
      setTrafficLight(initialCampaign.trafficLight ?? "");
      setOnboardingTarget(
        initialCampaign.onboardingTarget != null
          ? String(initialCampaign.onboardingTarget)
          : "",
      );
      setPostTarget(
        initialCampaign.postTarget != null
          ? String(initialCampaign.postTarget)
          : "",
      );
      setPostingDueDate(
        initialCampaign.postingDueDate
          ? initialCampaign.postingDueDate.slice(0, 10)
          : "",
      );
      setStartAt(
        initialCampaign.startAt ? initialCampaign.startAt.slice(0, 10) : "",
      );
      setEndAt(initialCampaign.endAt ? initialCampaign.endAt.slice(0, 10) : "");
      setBrief(initialCampaign.brief ?? "");
      setProductRefs(initialCampaign.productRefs ?? []);
      setProductRefDraft("");
      setProductAllocations(initialCampaign.productAllocations ?? []);
      setProductGroups(initialCampaign.productGroups ?? []);
      setProductSelectionMode(
        (initialCampaign.productGroups?.length ?? 0) > 0 ? "grouped" : "flat",
      );
      setLimitationRegions(regionLimitationValues(initialCampaign.limitations));
      setBriefForm(readBriefStructuredForm(initialCampaign.briefStructured));
      setCardForm(readCardFieldsForm(initialCampaign));
      setAffiliateTermsForm(readAffiliateTermsForm(initialCampaign));
    } else if (mode === "create" && open) {
      resetForm();
    }
  }, [open, mode, initialCampaign]);

  const createMutation = useMutation({
    mutationFn: (request: CreateInfluencerCampaignRequest) =>
      influencerCampaignAPIClient.createInfluencerCampaign(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_CAMPAIGN_KEYS.lists(),
      });
      onOpenChange(false);
      resetForm();
      toast.success("Campaign created");
    },
    onError: () => {
      toast.error("Failed to save campaign");
    },
  });

  const editMutation = useMutation({
    mutationFn: (request: UpdateInfluencerCampaignRequest) => {
      if (!initialCampaign)
        return Promise.reject(new Error("Cannot edit without initialCampaign"));
      return influencerCampaignAPIClient.updateInfluencerCampaign(
        initialCampaign.campaignId,
        request,
      );
    },
    onSuccess: async () => {
      if (!initialCampaign) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: INFLUENCER_CAMPAIGN_KEYS.lists(),
        }),
        queryClient.invalidateQueries({
          queryKey: INFLUENCER_CAMPAIGN_KEYS.detail(initialCampaign.campaignId),
        }),
      ]);
      onOpenChange(false);
      toast.success("Campaign updated");
    },
    onError: () => {
      toast.error("Failed to save campaign");
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;
  const groupedProductValidationErrors =
    productSelectionMode === "grouped"
      ? validateProductGroupConfiguration(productGroups, productAllocations)
      : [];
  const hasProductOptions = productAllocations.length > 0;

  const handleProductSelectionModeChange = (
    nextProductSelectionMode: ProductSelectionMode,
  ) => {
    if (hasProductOptions) return;
    setProductSelectionMode(nextProductSelectionMode);
    if (nextProductSelectionMode === "grouped" && productGroups.length === 0) {
      setProductGroups([
        {
          groupId: createProductGroupId(productGroups),
          name: "",
          choiceCount: 1,
        },
      ]);
    }
    if (nextProductSelectionMode === "flat") {
      setProductGroups([]);
    }
  };

  // An affiliate programme is open to every region, and the Facade rejects one with a region limitation.
  const isAffiliate = pipelineType === CampaignPipelineType.Affiliate;
  const regionRequired = !isAffiliate;
  const missingRegion = regionRequired && limitationRegions.length === 0;
  const affiliateTermsValidationErrors = isAffiliate ? affiliateTermsErrors(affiliateTermsForm) : [];

  // Edit sends the terms only when they changed, and null when the operator emptied them back to the defaults.
  const affiliateTermsForRequest = () => {
    if (!isAffiliate) return undefined;
    const builtTerms = buildAffiliateTerms(affiliateTermsForm);
    if (mode !== "edit") return builtTerms;
    const storedTerms = buildAffiliateTerms(readAffiliateTermsForm(initialCampaign));
    if (JSON.stringify(builtTerms) === JSON.stringify(storedTerms)) return undefined;
    return builtTerms ?? null;
  };

  const submit = () => {
    if (
      !name.trim() ||
      !brandId ||
      missingRegion ||
      groupedProductValidationErrors.length > 0 ||
      affiliateTermsValidationErrors.length > 0
    ) {
      return;
    }

    const briefStructured = buildBriefStructured(brief, briefForm);
    const cardFields = buildCardFields(cardForm);
    const hadStoredProductGroups =
      (initialCampaign?.productGroups?.length ?? 0) > 0;
    const productGroupsForRequest =
      productSelectionMode === "grouped"
        ? productGroups
        : hadStoredProductGroups
          ? []
          : undefined;
    const formState = {
      name,
      brandId,
      trafficLight,
      onboardingTarget,
      postTarget,
      postingDueDate,
      startAt,
      endAt,
      brief,
      productRefs,
      productAllocations,
      productGroups: productGroupsForRequest,
      limitationRegions: regionRequired ? limitationRegions : [],
      affiliateTerms: affiliateTermsForRequest(),
    };

    if (mode === "edit") {
      editMutation.mutate(buildUpdateCampaignRequest(formState, briefStructured, cardFields));
      return;
    }
    if (!defaultOwnerId) return;
    createMutation.mutate(
      buildCreateCampaignRequest(formState, status, defaultOwnerId, briefStructured, cardFields, pipelineType),
    );
  };

  const isEdit = mode === "edit";

  const patchCard = (changes: Partial<CardFieldsFormState>) =>
    setCardForm((prev) => ({ ...prev, ...changes }));

  const toggleType = (t: InfluencerCampaignType) =>
    setCardForm((prev) => ({
      ...prev,
      types: prev.types.includes(t)
        ? prev.types.filter((x) => x !== t)
        : [...prev.types, t],
    }));
  const togglePlatform = (p: InfluencerCampaignPlatform) =>
    setCardForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(p)
        ? prev.platforms.filter((x) => x !== p)
        : [...prev.platforms, p],
    }));
  const toggleBadge = (b: CampaignRewardBadge) =>
    setCardForm((prev) => ({
      ...prev,
      rewardBadges: prev.rewardBadges.includes(b)
        ? prev.rewardBadges.filter((x) => x !== b)
        : [...prev.rewardBadges, b],
    }));
  const toggleLimitationRegion = (code: string) =>
    setLimitationRegions((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );

  const scrollToSection = (id: string) => {
    formScrollRef.current
      ?.querySelector(`#section-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Scroll spy: highlight the nav step whose section is nearest the top of the scroll area.
  const handleFormScroll = () => {
    const container = formScrollRef.current;
    if (!container) return;
    let current: (typeof SECTIONS)[number]["id"] = SECTIONS[0].id;
    for (const s of SECTIONS) {
      const el = container.querySelector<HTMLElement>(`#section-${s.id}`);
      if (el && el.offsetTop - container.scrollTop <= SCROLL_SPY_OFFSET_PX) current = s.id;
    }
    setActiveSection(current);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-grid tw-max-h-[90vh] tw-max-w-4xl tw-grid-rows-[auto_1fr_auto]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Campaign" : "New Campaign"}</DialogTitle>
          <p className="tw-text-sm tw-text-muted-foreground">
            {isEdit
              ? "Update campaign details"
              : "Create a new influencer campaign"}
          </p>
        </DialogHeader>

        <div className="tw-grid tw-min-h-0 tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-[200px_1fr]">
          <nav
            aria-label="Campaign sections"
            className="tw-hidden tw-self-start md:tw-block"
          >
            <ul className="tw-flex tw-flex-col tw-gap-1">
              {SECTIONS.map((s) => {
                const isActive = activeSection === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(s.id)}
                      aria-current={isActive ? "step" : undefined}
                      className={cn(
                        "tw-flex tw-w-full tw-items-start tw-gap-2.5 tw-rounded-md tw-px-2.5 tw-py-2 tw-text-left tw-transition-colors",
                        isActive ? "tw-bg-primary/10" : "hover:tw-bg-muted/60",
                      )}
                    >
                      <span
                        className={cn(
                          "tw-mt-0.5 tw-inline-flex tw-h-5 tw-w-5 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-bold",
                          isActive
                            ? "tw-bg-primary tw-text-primary-foreground"
                            : "tw-bg-muted tw-text-muted-foreground",
                        )}
                      >
                        {s.num}
                      </span>
                      <span>
                        <span
                          className={cn(
                            "tw-block tw-text-[13px] tw-font-semibold",
                            isActive ? "tw-text-primary" : "tw-text-foreground",
                          )}
                        >
                          {s.title}
                        </span>
                        <span className="tw-block tw-text-[11px] tw-text-muted-foreground">
                          {s.sub}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="tw-mt-3 tw-rounded-md tw-bg-amber-50 tw-px-2.5 tw-py-2 tw-text-[11.5px] tw-leading-relaxed tw-text-amber-700">
              Required: <b>Campaign name</b> + <b>Brand</b>
              {regionRequired && <> + <b>Region</b></>}.
              Everything else is optional but feeds the creator screens.
            </p>
          </nav>

          <div
            ref={formScrollRef}
            onScroll={handleFormScroll}
            className="tw-grid tw-min-h-0 tw-gap-4 tw-overflow-y-auto tw-py-2 tw-pr-1 tw-[scroll-behavior:smooth] [&_*]:tw-min-w-0"
          >
            {/* 1. BASICS */}
            <section id="section-basics" className="tw-grid tw-gap-4 tw-scroll-mt-2">
              <SectionHeading num={1} title="Basics" />
              <Field label="Campaign name" required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Reboncel Q3 US Micro Wave"
                />
              </Field>
              <Field
                label="Pipeline"
                hint={isEdit ? "Fixed at creation" : "Cannot change after creation"}
              >
                {isEdit ? (
                  <Input
                    aria-label="Pipeline"
                    value={pipelineLabel(pipelineType)}
                    disabled
                  />
                ) : (
                  <select
                    aria-label="Pipeline"
                    value={pipelineType}
                    onChange={(e) => setPipelineType(e.target.value)}
                    className="tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
                  >
                    {Object.values(CampaignPipelineType).map((type) => (
                      <option key={type} value={type}>
                        {CAMPAIGN_PIPELINE_TYPE_LABEL[type]}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              {isAffiliate && (
                <AffiliateTermsFields
                  form={affiliateTermsForm}
                  errors={affiliateTermsValidationErrors}
                  onChange={(changes) => setAffiliateTermsForm((prev) => ({ ...prev, ...changes }))}
                />
              )}
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                <Field label="Brand" required>
                  <BrandSelect value={brandId} onChange={setBrandId} />
                </Field>
                <Field label="Owner">
                  <Input value={defaultOwnerId || "(current user)"} disabled />
                </Field>
              </div>
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                {!isEdit && (
                  <Field label="Status">
                    <select
                      value={status}
                      onChange={(e) =>
                        setStatus(e.target.value as InfluencerCampaignStatus)
                      }
                      className="tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
                    >
                      {STATUS_ORDER.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_META[s].label}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                {isEdit && (
                  <Field label="Status">
                    <p className="tw-mt-1 tw-text-[12px] tw-text-muted-foreground">
                      Status is updated from the detail panel
                    </p>
                  </Field>
                )}
                <Field label="Traffic light">
                  <select
                    value={trafficLight}
                    onChange={(e) =>
                      setTrafficLight(
                        e.target.value as InfluencerCampaignTrafficLight | "",
                      )
                    }
                    className="tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
                  >
                    <option value="">None</option>
                    <option value={InfluencerCampaignTrafficLight.Red}>
                      Red — at risk
                    </option>
                    <option value={InfluencerCampaignTrafficLight.Yellow}>
                      Yellow — needs attention
                    </option>
                    <option value={InfluencerCampaignTrafficLight.Green}>
                      Green — on track
                    </option>
                  </select>
                </Field>
              </div>
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                <Field label="Onboarding target">
                  <Input
                    type="number"
                    min={0}
                    value={onboardingTarget}
                    onChange={(e) => setOnboardingTarget(e.target.value)}
                    placeholder="e.g. 20"
                  />
                </Field>
                <Field label="Post target">
                  <Input
                    type="number"
                    min={0}
                    value={postTarget}
                    onChange={(e) => setPostTarget(e.target.value)}
                    placeholder="e.g. 15"
                  />
                </Field>
              </div>
              <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                <Field label="Start date">
                  <Input
                    type="date"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                  />
                </Field>
                <Field label="End date">
                  <Input
                    type="date"
                    value={endAt}
                    onChange={(e) => setEndAt(e.target.value)}
                  />
                </Field>
                <Field label="Posting due">
                  <Input
                    type="date"
                    value={postingDueDate}
                    onChange={(e) => setPostingDueDate(e.target.value)}
                  />
                </Field>
              </div>
              {regionRequired && (
                <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-p-4">
                  <label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-foreground/80">
                    <span aria-hidden>🌍</span> Region requirement
                    <span className="tw-text-red-500">*</span>
                    <span className="tw-font-normal tw-text-muted-foreground">· multi-select</span>
                  </label>
                  <p className="tw-mb-2.5 tw-text-[11.5px] tw-text-muted-foreground">
                    Restrict this campaign to creators in selected regions. Select
                    at least one region.
                  </p>
                  <div role="group" aria-label="Region requirement" className="tw-flex tw-flex-wrap tw-gap-2">
                    {CAMPAIGN_REGION_OPTIONS.map((opt) => {
                      const on = limitationRegions.includes(opt.code);
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          aria-pressed={on}
                          onClick={() => toggleLimitationRegion(opt.code)}
                          className={cn(
                            "tw-rounded-lg tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition-colors",
                            on
                              ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
                              : "tw-border-input tw-bg-background tw-text-foreground/70 hover:tw-border-primary/40",
                          )}
                        >
                          <span className="tw-font-mono tw-font-semibold">{opt.code}</span>
                          <span className="tw-ml-1 tw-text-muted-foreground">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            <hr className="tw-border-border/60" />

            {/* 2. CAMPAIGN BRIEF */}
            <section id="section-brief" className="tw-grid tw-gap-4 tw-scroll-mt-2">
              <SectionHeading
                num={2}
                title="Campaign Brief"
                badge={
                  <span className="tw-rounded-full tw-bg-blue-50 tw-px-2 tw-py-0.5 tw-text-[10.5px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-blue-600">
                    creator sees this
                  </span>
                }
              />
              <Field label="Core message" hint="hero paragraph">
                <textarea
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={3}
                  placeholder="The hero paragraph creators see on the campaign detail screen. Also stored as briefStructured.coreMessage."
                  className="tw-min-h-[72px] tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm"
                />
              </Field>
              <Field label="Tagline" hint="one-line caption shown under the card title">
                <Input
                  value={cardForm.tagline}
                  onChange={(e) => patchCard({ tagline: e.target.value })}
                  placeholder="Calm-the-redness ritual, on camera"
                  maxLength={240}
                />
              </Field>
              <BriefStructuredSection value={briefForm} onChange={setBriefForm} />
            </section>

            <hr className="tw-border-border/60" />

            {/* 3. REWARD & PRODUCTS */}
            <section id="section-reward" className="tw-grid tw-gap-4 tw-scroll-mt-2">
              <SectionHeading num={3} title="Reward & Products" />

              <div>
                <label className="tw-mb-1.5 tw-block tw-text-xs tw-font-semibold tw-text-foreground/80">
                  Campaign type
                  <span className="tw-ml-1 tw-font-normal tw-text-muted-foreground">· multi-select</span>
                </label>
                <div role="group" aria-label="Campaign type" className="tw-grid tw-grid-cols-2 tw-gap-2.5 sm:tw-grid-cols-3 lg:tw-grid-cols-5">
                  {Object.values(InfluencerCampaignType).map((t) => {
                    const on = cardForm.types.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleType(t)}
                        className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border tw-px-2.5 tw-py-3 tw-text-center tw-transition-colors ${
                          on
                            ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
                            : "tw-border-input tw-bg-background tw-text-foreground/80 hover:tw-border-primary/40"
                        }`}
                      >
                        <span aria-hidden className="tw-text-xl tw-leading-none">
                          {CAMPAIGN_TYPE_ICON[t]}
                        </span>
                        <span className="tw-text-xs tw-font-semibold">
                          {INFLUENCER_CAMPAIGN_TYPE_LABEL[t]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {cardForm.types.includes(InfluencerCampaignType.Giveaway) && (
                <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-primary/40 tw-bg-primary/5 tw-px-3.5 tw-py-3">
                  <label className="tw-mb-2 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-primary">
                    <span aria-hidden>🎉</span> Giveaway config
                  </label>
                  <div className="tw-grid tw-gap-2">
                    <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-xs tw-text-foreground/80">
                      <input
                        type="checkbox"
                        checked={cardForm.giveawayOpenGlobally}
                        onChange={(e) =>
                          patchCard({ giveawayOpenGlobally: e.target.checked })
                        }
                        className="tw-h-3.5 tw-w-3.5"
                      />
                      Open globally (every platform / region)
                    </label>
                    <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-xs tw-text-foreground/80">
                      <input
                        type="checkbox"
                        checked={cardForm.giveawayRequireContentForReward}
                        onChange={(e) =>
                          patchCard({
                            giveawayRequireContentForReward: e.target.checked,
                          })
                        }
                        className="tw-h-3.5 tw-w-3.5"
                      />
                      Require content submission before receiving the prize
                    </label>
                  </div>
                </div>
              )}

              <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-p-4">
                <label className="tw-mb-2.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-foreground/80">
                  <span aria-hidden>📱</span> Target platforms
                  <span className="tw-font-normal tw-text-muted-foreground">· multi-select</span>
                </label>
                <div role="group" aria-label="Target platforms" className="tw-flex tw-flex-wrap tw-gap-2">
                  {(isAffiliate ? AFFILIATE_CAMPAIGN_PLATFORMS : STANDARD_CAMPAIGN_PLATFORMS).map((p) => {
                    const on = cardForm.platforms.includes(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        aria-pressed={on}
                        onClick={() => togglePlatform(p)}
                        className={`tw-rounded-lg tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition-colors ${
                          on
                            ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
                            : "tw-border-input tw-bg-background tw-text-foreground/70 hover:tw-border-primary/40"
                        }`}
                      >
                        {INFLUENCER_CAMPAIGN_PLATFORM_LABEL[p]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-p-4">
                <label className="tw-mb-2.5 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-foreground/80">
                  <span aria-hidden>🏷️</span> Reward badges
                  <span className="tw-font-normal tw-text-muted-foreground">· chips shown on the campaign card</span>
                </label>
                <div role="group" aria-label="Reward badges" className="tw-flex tw-flex-wrap tw-gap-2">
                  {Object.values(CampaignRewardBadge).map((b) => {
                    const on = cardForm.rewardBadges.includes(b);
                    return (
                      <button
                        key={b}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleBadge(b)}
                        className={`tw-rounded-lg tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition-colors ${
                          on
                            ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
                            : "tw-border-input tw-bg-background tw-text-foreground/70 hover:tw-border-primary/40"
                        }`}
                      >
                        {CAMPAIGN_REWARD_BADGE_LABEL[b]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-2 tw-gap-3 lg:tw-grid-cols-4">
                <Field label="Fixed pay (USD)">
                  <Input
                    type="number"
                    min={0}
                    value={cardForm.rewardFixed}
                    onChange={(e) => patchCard({ rewardFixed: e.target.value })}
                    placeholder="e.g. 80"
                  />
                </Field>
                <Field label="Commission (%)">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={cardForm.rewardCommission}
                    onChange={(e) => patchCard({ rewardCommission: e.target.value })}
                    placeholder="e.g. 8"
                  />
                </Field>
                <Field label="Bonus (USD)">
                  <Input
                    type="number"
                    min={0}
                    value={cardForm.rewardBonus}
                    onChange={(e) => patchCard({ rewardBonus: e.target.value })}
                    placeholder="e.g. 30"
                  />
                </Field>
                <Field label="Product worth (USD)">
                  <Input
                    type="number"
                    min={0}
                    value={cardForm.productWorth}
                    onChange={(e) => patchCard({ productWorth: e.target.value })}
                    placeholder="e.g. 50"
                  />
                </Field>
              </div>

              <div className="tw-rounded-lg tw-border tw-border-border tw-bg-muted/20 tw-p-4">
                <label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-foreground/80">
                  <span aria-hidden>🛍️</span> Product selection
                </label>
                <div className="tw-mb-3 tw-grid tw-gap-1.5">
                  <label className="tw-text-[11px] tw-font-semibold tw-text-muted-foreground">
                    Product selection mode
                  </label>
                  <select
                    aria-label="Product selection mode"
                    value={productSelectionMode}
                    disabled={hasProductOptions}
                    onChange={(event) =>
                      handleProductSelectionModeChange(
                        event.target.value as ProductSelectionMode,
                      )
                    }
                    className="tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
                  >
                    <option value="flat">Single pool (legacy)</option>
                    <option value="grouped">Grouped choices</option>
                  </select>
                  {hasProductOptions && (
                    <p className="tw-text-[11px] tw-text-muted-foreground">
                      Remove all product options before changing modes.
                    </p>
                  )}
                </div>

                {productSelectionMode === "flat" ? (
                  <>
                    <p className="tw-mb-3 tw-text-[11.5px] tw-text-muted-foreground">
                      Creator picks <b>N</b> SKUs from one pool at apply time.
                      Leave choice at 0 to give them <b>all</b>.
                    </p>
                    <ProductPoolChoice
                      allocations={productAllocations}
                      onAllocationsChange={setProductAllocations}
                      choiceCount={cardForm.productChoiceCount}
                      onChoiceCountChange={(productChoiceCount) =>
                        patchCard({ productChoiceCount })
                      }
                    />
                  </>
                ) : (
                  <ProductGroupsEditor
                    productGroups={productGroups}
                    productAllocations={productAllocations}
                    onProductGroupsChange={setProductGroups}
                    onProductAllocationsChange={setProductAllocations}
                  />
                )}
              </div>

              <Field label="Product reference URLs">
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  {productRefs.length > 0 && (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      {productRefs.map((url) => (
                        <div
                          key={url}
                          className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-py-1.5 tw-text-sm"
                        >
                          <span className="tw-flex-1 tw-truncate" title={url}>
                            {url}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setProductRefs((refs) =>
                                refs.filter((r) => r !== url),
                              )
                            }
                            className="tw-text-muted-foreground hover:tw-text-foreground"
                            aria-label={`Remove ${url}`}
                          >
                            <X className="tw-h-3.5 tw-w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="tw-flex tw-gap-2">
                    <Input
                      value={productRefDraft}
                      onChange={(e) => setProductRefDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const v = productRefDraft.trim();
                          if (v && !productRefs.includes(v)) {
                            setProductRefs([...productRefs, v]);
                            setProductRefDraft("");
                          }
                        }
                      }}
                      placeholder="https://..."
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const v = productRefDraft.trim();
                        if (v && !productRefs.includes(v)) {
                          setProductRefs([...productRefs, v]);
                          setProductRefDraft("");
                        }
                      }}
                      disabled={!productRefDraft.trim()}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </Field>
            </section>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              isPending ||
              !name.trim() ||
              !brandId ||
              missingRegion ||
              groupedProductValidationErrors.length > 0 ||
              affiliateTermsValidationErrors.length > 0
            }
          >
            {isPending
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
